import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableContainer,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IOpportunityItem } from '../../../../models/opportunityItem.model';
import { PageFilter } from '../../../../state/app';
import { AppState } from '../../../../state/configureStore';
import {
  clearOpportunityItem,
  fetchOpportunityItemList,
  OpportunityItemListFilter,
  OpportunityItemState,
} from '../../../../state/opportunityItem';
import { TaskState } from '../../../../state/task';
import { createTaskItems, TaskItemState } from '../../../../state/taskItem';
import { SearchField, SortLabel, TableLoading } from '../../../components';
import useStyles from './taskItemCreateDialog.style';
import { useTableSorting } from '../../../hooks';
import { TransitionProps } from '@material-ui/core/transitions';
import {
  Close as CloseIcon,
  SkipNextOutlined as NextIcon,
  SkipPreviousOutlined as PreviousIcon,
  Done as DoneIcon,
} from '@material-ui/icons';
import { TablePageSize } from '../../../../constants';
import { Grid as MUIGrid } from '@mui/material';

interface TaskItemCreateDialogProps {
  open: boolean;
  handleClose: () => void;
}

type dialogStep =
  | 'selectAssignmentItem'
  | 'confirmAssignmentItem'
  | 'createdAssignmentItem';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return (
    <Slide direction="up" ref={ref} {...props} timeout={{ enter: 400, exit: 400 }} />
  );
});

const TaskItemCreateDialog: React.FC<TaskItemCreateDialogProps> = (props) => {
  const { open, handleClose } = props;
  const dispatch = useDispatch();
  const classes = useStyles();
  const [sort, setSort] = useTableSorting(
    {
      orderBy: 'Opportunity.AssignmentNumber',
      direction: 'desc',
    },
    true,
  );
  const [filters, setFilters] = useState<OpportunityItemListFilter>({});
  const [paging, setPaging] = useState<PageFilter>({
    pageNumber: 0,
    pageSize: 10,
  });
  const [selectedOpportunityItems, setSelectedOpportunityItems] = useState<
    IOpportunityItem[]
  >([]);
  const [step, setStep] = useState<dialogStep>('selectAssignmentItem');
  const { items: opportunityItems, totalItems, loading } = useSelector<
    AppState,
    OpportunityItemState
  >((state) => state.opportunityItem);

  const {
    item: { id: taskId },
  } = useSelector<AppState, TaskState>((state) => state.task);

  const { createdItems } = useSelector<AppState, TaskItemState>(
    (state) => state.taskItem,
  );

  useEffect(() => {
    const newPaging = { ...paging, sort };
    dispatch(fetchOpportunityItemList(filters, newPaging));
    return function cleanUp() {
      setStep('selectAssignmentItem');
      dispatch(clearOpportunityItem());
    };
  }, [dispatch, paging, filters, sort]);

  const handleChangeFilter = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { target } = event;
    setFilters((prevState) => ({
      ...prevState,
      [target.name]: target.value,
    }));
    setPaging({
      ...paging,
      pageNumber: 0,
    });
  };

  const handleChangePageNumber = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    setPaging({ ...paging, pageNumber: newPage });
  };

  const handleChangePageSize = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPaging({
      ...paging,
      pageSize: +event.target.value,
      pageNumber: 0,
    });
  };

  const visibleSelectedCount = opportunityItems.filter((opportunityItem) => {
    return (
      selectedOpportunityItems.findIndex(
        (selectedOpportunityItem) => selectedOpportunityItem.id === opportunityItem.id,
      ) !== -1
    );
  }).length;

  const allVisibleSelected =
    opportunityItems.length > 0 && visibleSelectedCount === opportunityItems.length;

  return (
    <Dialog
      classes={{ paper: classes.dialog }}
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth={true}
      disableBackdropClick={true}
      TransitionComponent={Transition}
    >
      {step === 'selectAssignmentItem' && (
        <>
          <DialogTitle className={classes.selectStepTitle} disableTypography>
            <Box className={classes.stepHero}>
              <Box>
                <Typography variant="h4" className={classes.stepHeading}>
                  Select Assignment Item
                </Typography>
                <Typography variant="body1" className={classes.stepSubtitle}>
                  Choose from available assignment items to include in the task. You can
                  select multiple items if needed.
                </Typography>
              </Box>
              <Box className={classes.selectionBadge}>
                {selectedOpportunityItems.length} selected
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent className={classes.selectStepContent}>
            <Box className={classes.filterSurface}>
              <Box className={classes.filterHeader}>
                <Box>
                  <Typography variant="h6" className={classes.surfaceTitle}>
                    Filter Assignments
                  </Typography>
                  <Typography variant="body2" className={classes.surfaceSubtitle}>
                    Search by assignment number, program, block, or item.
                  </Typography>
                </Box>
                <Typography className={classes.filterMeta}>
                  {totalItems} result{totalItems === 1 ? '' : 's'}
                </Typography>
              </Box>
              <MUIGrid container spacing={1}>
                <MUIGrid xs={3}>
                  <SearchField
                    label="Assignment Number"
                    onChange={handleChangeFilter}
                    filterKey="Opportunity.AssignmentNumber.startsWith"
                    filters={filters}
                    fullWidth
                  />
                </MUIGrid>
                <MUIGrid xs={3}>
                  <SearchField
                    label="Program"
                    onChange={handleChangeFilter}
                    filterKey="Opportunity.Program.Name.startsWith"
                    filters={filters}
                    fullWidth
                  />
                </MUIGrid>
                <MUIGrid xs={3}>
                  <SearchField
                    label="Block No"
                    onChange={handleChangeFilter}
                    filterKey="Block.BlockNo.startsWith"
                    filters={filters}
                    fullWidth
                  />
                </MUIGrid>
                <MUIGrid xs={3}>
                  <SearchField
                    label="Item"
                    onChange={handleChangeFilter}
                    filterKey="Block.Item.startsWith"
                    filters={filters}
                    fullWidth
                  />
                </MUIGrid>
              </MUIGrid>
            </Box>

            <Box className={classes.tableSurface}>
              <Box className={classes.tableHeader}>
                <Box>
                  <Typography variant="h6" className={classes.surfaceTitle}>
                    Assignment Results
                  </Typography>
                  <Typography variant="body2" className={classes.surfaceSubtitle}>
                    Select one or more rows, then continue to confirmation.
                  </Typography>
                </Box>
                <Typography className={classes.filterMeta}>
                  Visible selected: {visibleSelectedCount}
                </Typography>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow className={classes.tableHeadRow}>
                      <TableCell padding="checkbox" className={classes.tableHeadCell}>
                        <Checkbox
                          indeterminate={visibleSelectedCount > 0 && !allVisibleSelected}
                          checked={allVisibleSelected}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                            checked,
                          ) => {
                            if (checked) {
                              const isItemAdded = (opItem: IOpportunityItem) => {
                                return (
                                  selectedOpportunityItems.findIndex(
                                    (selectedOpportunityItem) =>
                                      selectedOpportunityItem.id === opItem.id,
                                  ) === -1
                                );
                              };
                              const itemToAdd = opportunityItems.filter(isItemAdded);
                              setSelectedOpportunityItems([
                                ...selectedOpportunityItems,
                                ...itemToAdd,
                              ]);
                            } else {
                              setSelectedOpportunityItems([]);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell className={classes.tableHeadCell}>No.</TableCell>
                      <TableCell className={classes.tableHeadCell}>
                        <SortLabel
                          sort={sort}
                          handleSortChange={setSort}
                          field="Opportunity.AssignmentNumber"
                        >
                          Assignment Number
                        </SortLabel>
                      </TableCell>
                      <TableCell className={classes.tableHeadCell}>Company</TableCell>
                      <TableCell className={classes.tableHeadCell}>Program</TableCell>
                      <TableCell className={classes.tableHeadCell}>
                        <SortLabel
                          sort={sort}
                          handleSortChange={setSort}
                          field="Block.BlockNo"
                        >
                          Block No
                        </SortLabel>
                      </TableCell>
                      <TableCell className={classes.tableHeadCell}>Item</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableLoading isLoading={loading} colSpan={10}>
                      {opportunityItems.map((opportunityItem, index) => {
                        const { id } = opportunityItem;
                        const isCheckboxChecked =
                          selectedOpportunityItems.findIndex(
                            (selectedOpportunityItem) =>
                              selectedOpportunityItem.id === id,
                          ) !== -1;
                        return (
                          <TableRow
                            key={opportunityItem.id}
                            hover
                            className={classes.resultRow}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                onChange={(
                                  event: React.ChangeEvent<HTMLInputElement>,
                                  checked,
                                ) => {
                                  if (checked) {
                                    setSelectedOpportunityItems([
                                      ...selectedOpportunityItems,
                                      opportunityItem,
                                    ]);
                                  } else {
                                    const newSelectedOpportunityItems = [
                                      ...selectedOpportunityItems,
                                    ];
                                    const selectedIndex = selectedOpportunityItems.findIndex(
                                      (selectedOpportunityItem) =>
                                        selectedOpportunityItem.id === id,
                                    );
                                    newSelectedOpportunityItems.splice(selectedIndex, 1);
                                    setSelectedOpportunityItems(
                                      newSelectedOpportunityItems,
                                    );
                                  }
                                }}
                                checked={isCheckboxChecked}
                              />
                            </TableCell>
                            <TableCell>
                              {index + paging.pageSize * paging.pageNumber + 1}
                            </TableCell>
                            <TableCell>
                              {opportunityItem.opportunity?.assignmentNumber}
                            </TableCell>
                            <TableCell>
                              {opportunityItem.opportunity?.company?.name}
                            </TableCell>
                            <TableCell>{opportunityItem.block?.program?.name}</TableCell>
                            <TableCell>{opportunityItem.block?.blockNo}</TableCell>
                            <TableCell>{opportunityItem.block?.item}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableLoading>
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                className={classes.tablePagination}
                rowsPerPageOptions={[...TablePageSize]}
                component="div"
                count={totalItems}
                rowsPerPage={paging.pageSize}
                page={paging.pageNumber}
                onPageChange={handleChangePageNumber}
                onRowsPerPageChange={handleChangePageSize}
              />
            </Box>
          </DialogContent>
          <DialogActions className={classes.selectStepActions}>
            <Typography className={classes.actionHint}>
              Choose the assignment items you want to include, then continue to review
              everything before creation.
            </Typography>
            <Box>
              <Button
                onClick={handleClose}
                color="default"
                variant="contained"
                startIcon={<CloseIcon />}
                className={classes.secondaryAction}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setStep('confirmAssignmentItem');
                }}
                color="primary"
                variant="contained"
                startIcon={<NextIcon />}
                className={classes.primaryAction}
              >
                Next
              </Button>
            </Box>
          </DialogActions>
        </>
      )}
      {step === 'confirmAssignmentItem' && (
        <>
          <DialogTitle className={classes.selectStepTitle} disableTypography>
            <Box className={classes.stepHero}>
              <Box>
                <Typography variant="h4" className={classes.stepHeading}>
                  Confirm Assignment Item
                </Typography>
                <Typography variant="body1" className={classes.stepSubtitle}>
                  Review the selected assignment items before confirming and creating them
                  for this task.
                </Typography>
              </Box>
              <Box className={classes.selectionBadge}>
                {selectedOpportunityItems.length} ready to confirm
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent className={classes.selectStepContent}>
            <Box className={classes.tableSurface}>
              <Box className={classes.tableHeader}>
                <Box>
                  <Typography variant="h6" className={classes.surfaceTitle}>
                    Selected Assignment Items
                  </Typography>
                  <Typography variant="body2" className={classes.surfaceSubtitle}>
                    Verify the assignment number, program, block, and item before you
                    continue.
                  </Typography>
                </Box>
                <Typography className={classes.filterMeta}>
                  {selectedOpportunityItems.length} item
                  {selectedOpportunityItems.length === 1 ? '' : 's'}
                </Typography>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow className={classes.tableHeadRow}>
                      <TableCell className={classes.tableHeadCell}>No.</TableCell>
                      <TableCell className={classes.tableHeadCell}>
                        Assignment Number
                      </TableCell>
                      <TableCell className={classes.tableHeadCell}>Program</TableCell>
                      <TableCell className={classes.tableHeadCell}>Block No</TableCell>
                      <TableCell className={classes.tableHeadCell}>Item</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedOpportunityItems.map((selectedOpportunityItem, index) => {
                      return (
                        <TableRow
                          key={selectedOpportunityItem.id}
                          hover
                          className={classes.resultRow}
                        >
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            {selectedOpportunityItem.opportunity?.assignmentNumber}
                          </TableCell>
                          <TableCell>
                            {selectedOpportunityItem.block?.program?.name}
                          </TableCell>
                          <TableCell>{selectedOpportunityItem.block?.blockNo}</TableCell>
                          <TableCell>{selectedOpportunityItem.block?.item}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </DialogContent>
          <DialogActions className={classes.selectStepActions}>
            <Typography className={classes.actionHint}>
              Go back if you need to change the selection, or confirm now to create the
              chosen assignment items.
            </Typography>
            <Box>
              <Button
                onClick={() => {
                  setStep('selectAssignmentItem');
                }}
                color="default"
                variant="contained"
                startIcon={<PreviousIcon />}
                className={classes.secondaryAction}
              >
                Back
              </Button>
              <Button
                onClick={() => {
                  const taskItems = selectedOpportunityItems.map(
                    (selectedOpportunityItem) => ({
                      smTaskId: taskId,
                      opportunityItemId: selectedOpportunityItem.id,
                      opportunityItem: selectedOpportunityItem,
                    }),
                  );
                  dispatch(createTaskItems(taskItems));
                  setStep('createdAssignmentItem');
                }}
                color="primary"
                variant="contained"
                startIcon={<DoneIcon />}
                className={classes.primaryAction}
              >
                Confirm
              </Button>
            </Box>
          </DialogActions>
        </>
      )}
      {step === 'createdAssignmentItem' && (
        <>
          <DialogTitle className={classes.selectStepTitle} disableTypography>
            <Box className={classes.stepHero}>
              <Box>
                <Typography variant="h4" className={classes.stepHeading}>
                  Created Assignment Item
                </Typography>
                <Typography variant="body1" className={classes.stepSubtitle}>
                  Your assignment items have been created. Review the resulting rows and
                  close the dialog when you are done.
                </Typography>
              </Box>
              <Box className={classes.selectionBadge}>{createdItems.length} created</Box>
            </Box>
          </DialogTitle>
          <DialogContent className={classes.selectStepContent}>
            <Box className={classes.tableSurface}>
              <Box className={classes.tableHeader}>
                <Box>
                  <Typography variant="h6" className={classes.surfaceTitle}>
                    Created Assignment Items
                  </Typography>
                  <Typography variant="body2" className={classes.surfaceSubtitle}>
                    Review the final assignments and their current creation status.
                  </Typography>
                </Box>
                <Typography className={classes.filterMeta}>
                  {createdItems.length} item{createdItems.length === 1 ? '' : 's'}
                </Typography>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow className={classes.tableHeadRow}>
                      <TableCell className={classes.tableHeadCell}>No.</TableCell>
                      <TableCell className={classes.tableHeadCell}>
                        Assignment Number
                      </TableCell>
                      <TableCell className={classes.tableHeadCell}>Program</TableCell>
                      <TableCell className={classes.tableHeadCell}>Block No</TableCell>
                      <TableCell className={classes.tableHeadCell}>Item</TableCell>
                      <TableCell className={classes.tableHeadCell}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {createdItems.map((createdItem, index) => {
                      return (
                        <TableRow
                          key={createdItem.id}
                          hover
                          className={classes.resultRow}
                        >
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            {createdItem.opportunityItem?.opportunity?.assignmentNumber}
                          </TableCell>
                          <TableCell>
                            {createdItem.opportunityItem?.block?.program?.name}
                          </TableCell>
                          <TableCell>
                            {createdItem.opportunityItem?.block?.blockNo}
                          </TableCell>
                          <TableCell>
                            {createdItem.opportunityItem?.block?.item}
                          </TableCell>
                          <TableCell>{createdItem.status}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </DialogContent>
          <DialogActions className={classes.selectStepActions}>
            <Typography className={classes.actionHint}>
              The assignment items are ready. Close this dialog to return to the task and
              continue working.
            </Typography>
            <Box>
              <Button
                onClick={() => {
                  handleClose();
                  setStep('selectAssignmentItem');
                }}
                color="primary"
                variant="contained"
                startIcon={<CloseIcon />}
                className={classes.primaryAction}
              >
                Close
              </Button>
            </Box>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default TaskItemCreateDialog;
